/**
 * <bb-quick-view> — custom quick view modal for bb-product-card. Unlike Horizon's
 * #quick-add-dialog (which morphs in the full PDP), this fetches a dedicated
 * bb-quick-view section (image, title, rating, short description, option picker,
 * add-to-cart) and renders it inside a single shared <dialog> appended to <body>.
 *
 * Each bb-product-card is itself a <bb-quick-view> wrapper carrying data-product-url;
 * clicking a descendant [data-bb-quick-view-open] opens the modal for that product.
 * The dialog, its fetch cache and busy state are static so every card instance on
 * the page shares one modal. Add to cart follows the same pattern as bb-bundle.js:
 * dispatch @shopify/events' CartLinesUpdateEvent so Horizon's cart drawer updates.
 */
class BBQuickView extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  static #dialog = null;
  /** @type {HTMLElement | null} */
  static #content = null;
  /** @type {Map<string, string>} */
  static #cache = new Map();
  /** @type {AbortController | null} */
  static #abort = null;
  /** @type {HTMLElement | null} */
  static #lastTrigger = null;
  static #busy = false;

  connectedCallback() {
    this.addEventListener('click', (event) => this.#onClick(event));
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-bb-quick-view-open]') : null;
    if (!trigger) return;
    event.preventDefault();
    const url = this.dataset.productUrl || '';
    if (!url) return;
    BBQuickView.#lastTrigger = trigger instanceof HTMLElement ? trigger : null;
    BBQuickView.#open(url, this);
  }

  /** @param {string} url @param {BBQuickView} host - dispatches CartLinesUpdateEvent from here */
  static #open(url, host) {
    const dialog = BBQuickView.#ensureDialog();
    BBQuickView.#abort?.abort();
    const abort = new AbortController();
    BBQuickView.#abort = abort;

    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';

    const cached = BBQuickView.#cache.get(url);
    if (cached) {
      BBQuickView.#content.innerHTML = cached;
      BBQuickView.#wire(host);
      return;
    }

    BBQuickView.#content.innerHTML = '<div class="bb-qv__loading">Cargando…</div>';
    BBQuickView.#fetchSection(url, abort.signal)
      .then((html) => {
        BBQuickView.#cache.set(url, html);
        BBQuickView.#content.innerHTML = html;
        BBQuickView.#wire(host);
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('[bb-quick-view] fetch failed', error);
        window.location.href = url;
      });
  }

  static #close() {
    if (!BBQuickView.#dialog?.open) return;
    BBQuickView.#dialog.close();
  }

  /** @param {string} url @param {AbortSignal} signal */
  static async #fetchSection(url, signal) {
    const separator = url.includes('?') ? '&' : '?';
    const response = await fetch(`${url}${separator}section_id=bb-quick-view`, { signal });
    if (!response.ok) throw new Error(`Quick view fetch failed: ${response.status}`);
    const markup = await response.text();
    const doc = new DOMParser().parseFromString(markup, 'text/html');
    const inner = doc.querySelector('.bb-qv__inner');
    const script = doc.querySelector('[data-bb-qv-variants]');
    if (!inner) throw new Error('Quick view section markup missing .bb-qv__inner');
    return inner.outerHTML + (script ? script.outerHTML : '');
  }

  static #ensureDialog() {
    if (BBQuickView.#dialog) return BBQuickView.#dialog;

    const dialog = document.createElement('dialog');
    dialog.className = 'bb-qv';
    dialog.setAttribute('aria-labelledby', 'bb-qv-heading');

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'bb-qv__close';
    closeBtn.setAttribute('aria-label', 'Cerrar');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => BBQuickView.#close());

    const content = document.createElement('div');
    content.className = 'bb-qv__content';

    dialog.append(closeBtn, content);
    document.body.append(dialog);

    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) BBQuickView.#close();
    });
    dialog.addEventListener('close', () => {
      BBQuickView.#abort?.abort();
      document.documentElement.style.overflow = '';
      BBQuickView.#lastTrigger?.focus();
    });

    BBQuickView.#dialog = dialog;
    BBQuickView.#content = content;
    return dialog;
  }

  /** @param {BBQuickView} host */
  static #wire(host) {
    const inner = BBQuickView.#content.querySelector('.bb-qv__inner');
    if (!inner) return;

    const heading = inner.querySelector('.bb-qv__title');
    if (heading) heading.id = 'bb-qv-heading';

    const variantsScript = BBQuickView.#content.querySelector('[data-bb-qv-variants]');
    let variants = [];
    try {
      variants = variantsScript ? JSON.parse(variantsScript.textContent || '[]') : [];
    } catch (error) {
      console.error('[bb-quick-view] invalid variants JSON', error);
    }

    const groups = Array.from(inner.querySelectorAll('[data-bb-qv-group]'));
    const addButton = inner.querySelector('[data-bb-qv-add]');
    const addLabel = inner.querySelector('[data-bb-qv-add-label]');
    const addPrice = inner.querySelector('[data-bb-qv-add-price]');
    const errorBox = inner.querySelector('[data-bb-qv-error]');

    /** @type {Array<string | undefined>} */
    const selected = [];
    groups.forEach((group, index) => {
      const active = group.querySelector('[data-bb-qv-value][data-selected="true"]');
      selected[index] = active instanceof HTMLElement ? active.dataset.value : undefined;
    });

    const initialVariantId = addButton?.dataset.variantId;
    // Default-variant products render no option groups, so match the variant Liquid rendered.
    const resolveVariant = () =>
      groups.length === 0
        ? variants.find((variant) => String(variant.id) === initialVariantId) ?? variants[0]
        : variants.find((variant) => variant.options.every((value, index) => value === selected[index]));

    const updateAdd = () => {
      const variant = resolveVariant();
      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Agotado';
        if (addPrice) addPrice.hidden = true;
        return;
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.dataset.variantId = String(variant.id);
      }
      if (addLabel) addLabel.textContent = variant.available ? 'Añadir a la cesta' : 'Agotado';
      if (addPrice) {
        addPrice.hidden = !variant.available;
        addPrice.textContent = variant.price;
      }
    };

    groups.forEach((group, index) => {
      group.querySelectorAll('[data-bb-qv-value]').forEach((btn) => {
        btn.addEventListener('click', () => {
          selected[index] = btn.dataset.value;
          group.querySelectorAll('[data-bb-qv-value]').forEach((sibling) => {
            const isSelected = sibling === btn;
            sibling.dataset.selected = String(isSelected);
            sibling.setAttribute('aria-pressed', String(isSelected));
          });
          updateAdd();
        });
      });
    });

    updateAdd();

    addButton?.addEventListener('click', () => BBQuickView.#onAddToCart(addButton, errorBox, host));
  }

  /** @param {HTMLButtonElement} button @param {HTMLElement | null} errorBox @param {BBQuickView} host */
  static async #onAddToCart(button, errorBox, host) {
    if (BBQuickView.#busy || button.disabled) return;
    const variantId = Number(button.dataset.variantId);
    if (!Number.isInteger(variantId) || variantId <= 0) return;

    BBQuickView.#busy = true;
    const labelEl = button.querySelector('[data-bb-qv-add-label]');
    const originalLabel = labelEl?.textContent || '';
    if (labelEl) labelEl.textContent = 'Añadiendo…';
    button.disabled = true;
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }

    try {
      await BBQuickView.#addToCart(variantId, host);
      BBQuickView.#close();
    } catch (error) {
      console.error('[bb-quick-view] add to cart failed', error);
      if (errorBox) {
        errorBox.textContent = 'No se ha podido añadir el producto. Inténtalo de nuevo.';
        errorBox.hidden = false;
      }
      if (labelEl) labelEl.textContent = originalLabel;
      button.disabled = false;
    } finally {
      BBQuickView.#busy = false;
    }
  }

  /** @param {number} variantId @param {BBQuickView} host - dispatches CartLinesUpdateEvent from here */
  static async #addToCart(variantId, host) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean);

    let CartLinesUpdateEvent = null;
    try {
      ({ CartLinesUpdateEvent } = await import('@shopify/events'));
    } catch (error) {
      console.warn('[bb-quick-view] @shopify/events unavailable, falling back to cart page', error);
    }

    const routes = window.Theme?.routes || {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = routes.cart_url ? `${routes.cart_url}.js` : '/cart.js';

    let deferred = null;
    if (CartLinesUpdateEvent) {
      deferred = CartLinesUpdateEvent.createPromise();
      host.dispatchEvent(
        new CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(variantId), quantity: 1 }],
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
      });
      const payload = await response.json();
      if (payload.status) {
        throw new Error(payload.description || payload.message || 'Add to cart failed');
      }

      if (!CartLinesUpdateEvent || !deferred) return;

      const cart = await fetch(cartUrl, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'bb-quick-view',
          sourceId: 'bb-quick-view',
          itemCount: 1,
          sections: payload.sections,
          didError: false,
        },
      });
    } catch (error) {
      deferred?.reject(error);
      throw error;
    }
  }
}

if (!customElements.get('bb-quick-view')) {
  customElements.define('bb-quick-view', BBQuickView);
}
